import { useMemo } from 'react';
import {
  ComposedChart, Line, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import { useMarketingStore } from '../../store/marketing.store';
import { Download } from 'lucide-react';
import InfoTooltip from '../revenue/InfoTooltip';
import { format, addDays, addMonths, parse, isBefore, isEqual } from 'date-fns';

function formatVND(value: number): string {
  return value.toLocaleString('en-US');
}

interface TrendEntry {
  date: string;
  spend: number;
  leads: number;
}

function fillGaps(data: TrendEntry[], from: string, to: string, groupBy: 'day' | 'month'): TrendEntry[] {
  const map = new Map(data.map(d => [d.date, { ...d }]));

  const filled: TrendEntry[] = [];
  if (groupBy === 'day') {
    let cur = parse(from, 'yyyy-MM-dd', new Date());
    const end = parse(to, 'yyyy-MM-dd', new Date());
    while (isBefore(cur, end) || isEqual(cur, end)) {
      const key = format(cur, 'yyyy-MM-dd');
      filled.push(map.get(key) || { date: key, spend: 0, leads: 0 });
      map.delete(key);
      cur = addDays(cur, 1);
    }
  } else {
    const fromMonth = from.substring(0, 7);
    const endMonth = to.substring(0, 7);
    let cur = parse(fromMonth + '-01', 'yyyy-MM-dd', new Date());
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const key = format(cur, 'yyyy-MM');
      filled.push(map.get(key) || { date: key, spend: 0, leads: 0 });
      map.delete(key);
      if (key >= endMonth) break;
      cur = addMonths(cur, 1);
    }
  }

  // Merge any remaining out-of-range entries into the last in-range entry
  if (map.size > 0 && filled.length > 0) {
    const lastEntry = filled[filled.length - 1];
    for (const extra of map.values()) {
      lastEntry.spend += extra.spend;
      lastEntry.leads += extra.leads;
    }
  }

  return filled;
}

export default function MarketingTrendChart({ isDark }: { isDark: boolean }) {
  const marketingTrend = useMarketingStore((s) => s.marketingTrend);
  const reportFrom = useMarketingStore((s) => s.reportFrom);
  const reportTo = useMarketingStore((s) => s.reportTo);
  const trendGroupBy = useMarketingStore((s) => s.trendGroupBy);
  const fetchTrend = useMarketingStore((s) => s.fetchTrend);

  const tooltipText = "Chi phí quảng cáo Facebook Ads và số leads theo thời gian.\n\n• Đường cyan: Chi phí (Meta API field: spend)\n• Cột xanh: Số leads (Meta API field: actions[onsite_conversion.lead])\n\nDữ liệu gộp từ tất cả Ad Accounts (1A + 2A).\nNguồn: Facebook Ads Insights API";

  const trendData = useMemo(() => {
    if (!marketingTrend.length) return [];
    return marketingTrend.map(t => ({ date: t.date, spend: t.spend, leads: t.leads }));
  }, [marketingTrend]);

  const filledData = useMemo(() => {
    if (!trendData.length) return [];
    return fillGaps(trendData, reportFrom, reportTo, trendGroupBy);
  }, [trendData, reportFrom, reportTo, trendGroupBy]);

  const handleGroupByChange = (g: 'day' | 'month') => {
    if (g === trendGroupBy) return;
    fetchTrend(g);
  };

  const toggleClass = (active: boolean) =>
    `px-2.5 py-1 text-xs font-medium rounded transition-colors ${
      active
        ? 'bg-cyan-500 text-white'
        : isDark
          ? 'text-slate-400 hover:text-white hover:bg-white/10'
          : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'
    }`;

  const exportCSV = () => {
    const label = trendGroupBy === 'day' ? 'Ngày' : 'Tháng';
    const csvRows = [`${label},Chi phí,Leads`];
    for (const row of filledData) {
      csvRows.push(`"${row.date}",${row.spend},${row.leads}`);
    }
    const blob = new Blob(['\uFEFF' + csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `marketing_trend_${trendGroupBy}_${reportFrom}_${reportTo}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!marketingTrend.length) {
    return (
      <div className={`backdrop-blur rounded-lg p-6 ${isDark ? 'bg-white/10' : 'bg-white border border-gray-200 shadow-sm'}`}>
        <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
          Xu hướng chi phí & leads
          <InfoTooltip isDark={isDark} text={tooltipText} />
          {' '}
          <span className={`ml-2 text-sm font-normal ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
            ({reportFrom} — {reportTo})
          </span>
        </h3>
        <div className={`flex items-center justify-center h-64 text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
          Chưa có dữ liệu
        </div>
      </div>
    );
  }

  return (
    <div className={`backdrop-blur rounded-lg p-6 ${isDark ? 'bg-white/10' : 'bg-white border border-gray-200 shadow-sm'}`}>
      <div className="flex flex-wrap items-center gap-2 mb-1">
        <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
          Xu hướng chi phí & leads
          <InfoTooltip isDark={isDark} text={tooltipText} />
          {' '}
          <span className={`ml-2 text-sm font-normal ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
            ({reportFrom} — {reportTo})
          </span>
        </h3>
        <div className="flex-1" />
        <div className={`inline-flex rounded-lg overflow-hidden border ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
          <button onClick={() => handleGroupByChange('day')} className={toggleClass(trendGroupBy === 'day')}>Theo ngày</button>
          <button onClick={() => handleGroupByChange('month')} className={toggleClass(trendGroupBy === 'month')}>Theo tháng</button>
        </div>
        <button onClick={exportCSV} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${isDark ? 'bg-white/5 text-slate-300 hover:bg-white/10 hover:text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-900'}`}>
          <Download className="w-3.5 h-3.5" />
          Export CSV
        </button>
      </div>

      <ResponsiveContainer width="100%" height={360}>
        <ComposedChart data={filledData} margin={{ top: 10, right: 30, left: 20, bottom: 10 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#334155' : '#e5e7eb'} />
          <XAxis
            dataKey="date"
            tick={{ fill: isDark ? '#94a3b8' : '#6b7280', fontSize: 11 }}
            interval={trendGroupBy === 'day' && filledData.length > 14 ? Math.ceil(filledData.length / 14) : 0}
          />
          <YAxis
            yAxisId="left"
            tick={{ fill: isDark ? '#94a3b8' : '#6b7280', fontSize: 11 }}
            tickFormatter={formatVND}
          />
          <YAxis
            yAxisId="right"
            orientation="right"
            tick={{ fill: isDark ? '#94a3b8' : '#6b7280', fontSize: 11 }}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: isDark ? '#1e293b' : '#ffffff',
              border: `1px solid ${isDark ? '#475569' : '#e5e7eb'}`,
              borderRadius: '8px',
              color: isDark ? '#f8fafc' : '#111827',
            }}
            formatter={(value: any, name: any) => [
              name === 'spend' ? formatVND(value as number) + ' VND' : value,
              name === 'spend' ? 'Chi phí' : 'Leads (Facebook)',
            ]}
            labelStyle={{ color: isDark ? '#f8fafc' : '#111827', fontWeight: 600 }}
          />
          <Bar yAxisId="right" dataKey="leads" fill="#3b82f6" opacity={0.3} radius={[4, 4, 0, 0]} barSize={trendGroupBy === 'month' ? 40 : 20} />
          <Line yAxisId="left" type="monotone" dataKey="spend" stroke="#06b6d4" strokeWidth={2} dot={{ fill: '#06b6d4', r: 3 }} />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}
